import React from 'react';

import {
  FileAlt,
  Tasks,
  Wrench,
  Comments,
  ClipboardList,
  Pen,
  ExclamationTriangle,
  Cog,
  LinkLight,
} from '@assets/svgs';

import type { QuoteGetResponse } from '@lio/api-types';
import { QuoteType } from '@models/quotes';

import { UserRoles, allButCsr, allRoles, internalRoles } from '@models/user';
import { DrawerListItem } from '@components/drawer';
import { Summary, QuoteTree } from '@pages/QuoteDetails/sidebarItems/index';

const NoteDrawerItem = React.lazy(() =>
  import('../sidebarItems/notes/Notes').then((m) => ({ default: m.NoteDrawerItem })),
);
const MessageDrawerItem = React.lazy(() =>
  import('../sidebarItems/messages/Messages').then((m) => ({ default: m.MessageDrawerItem })),
);
const ToolsDrawerItem = React.lazy(() =>
  import('../sidebarItems/tools/Tools').then((m) => ({ default: m.ToolsDrawerItem })),
);
const UnderwritingDrawerItem = React.lazy(() =>
  import('../sidebarItems/underwriting/Underwriting').then((m) => ({ default: m.UnderwritingDrawerItem })),
);
const Subjectivities = React.lazy(() =>
  import('../sidebarItems/subjectivities/Subjectivities').then((m) => ({ default: m.Subjectivities })),
);
const RelatedTransactionsDrawerItem = React.lazy(() =>
  import('../sidebarItems/relatedTransactions').then((m) => ({ default: m.RelatedTransactions })),
);

import { BrokerageUserRole, LioUserRole } from '@lio/rule-engine';
import { DrawerList } from './DrawerList.enum';
import { LayoutPreferences } from '@pages/QuoteDetails/sidebarItems/LayoutPreferences/LayoutPreferences';

interface QuoteDrawerListItem extends DrawerListItem {
  hideOnBind?: boolean;
}

export enum QuoteDrawerUrlParams {
  Summary = 'summary',
  Navigator = 'navigator',
  Subjectivities = 'subjectivities',
  Underwriting = 'underwriting',
  Tools = 'tools',
  Messages = 'messages',
  Notes = 'notes',
  LayoutSettings = 'settings',
  Help = 'help',
  RelatedTransactions = 'related-transactions',
}
export const drawerListItems: QuoteDrawerListItem[] = [
  {
    icon: <FileAlt />,
    content: <Summary />,
    roles: allButCsr,
    name: DrawerList.Summary,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Summary,
    sidebarTabSearchParamValue: 'details',
  },
  {
    icon: <Tasks />,
    content: <QuoteTree />,
    roles: allButCsr,
    name: DrawerList.Navigator,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Navigator,
    sidebarTabSearchParamValue: undefined,
    hideOnBind: true,
  },
  {
    icon: <ExclamationTriangle />,
    content: <Subjectivities />,
    roles: Object.values({ ...LioUserRole, ...BrokerageUserRole }),
    name: DrawerList.Subjectivities,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Subjectivities,
    sidebarTabSearchParamValue: undefined,
    isShown: (quote?: QuoteGetResponse) =>
      Boolean(
        quote?.quoteType &&
        [QuoteType.NewBusiness, QuoteType.Rewrite, QuoteType.Renewal].includes(quote.quoteType as QuoteType),
      ),
  },
  {
    icon: <Pen />,
    content: <UnderwritingDrawerItem />,
    roles: internalRoles,
    name: DrawerList.Underwriting,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Underwriting,
    sidebarTabSearchParamValue: 'underwriting-issues',
  },
  {
    icon: <Wrench />,
    content: <ToolsDrawerItem />,
    roles: [
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
      UserRoles.SystemAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.PortalAdmin,
    ],
    name: DrawerList.Tools,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Tools,
    sidebarTabSearchParamValue: 'external-services',
    hideOnBind: true,
  },
  {
    icon: <Comments />,
    content: <MessageDrawerItem />,
    roles: allRoles.filter((el) => el !== UserRoles.CSR && el !== UserRoles.Quoter),
    name: DrawerList.Messages,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Messages,
    sidebarTabSearchParamValue: undefined,
    isShown: (quote?: QuoteGetResponse) => quote?.quoteType !== QuoteType.Endorsement,
  },
  {
    icon: <ClipboardList />,
    content: <NoteDrawerItem />,
    roles: [
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
      UserRoles.SystemAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.LioSales,
      UserRoles.PortalAdmin,
    ],
    name: DrawerList.Notes,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.Notes,
    sidebarTabSearchParamValue: undefined,
  },
  {
    icon: <LinkLight />,
    content: <RelatedTransactionsDrawerItem />,
    roles: internalRoles,
    name: DrawerList.RelatedTransactions,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.RelatedTransactions,
    sidebarTabSearchParamValue: 'policy',
  },
  {
    icon: <Cog />,
    content: <LayoutPreferences />,
    roles: allRoles,
    name: DrawerList.LayoutSettings,
    sidebarItemSearchParamValue: QuoteDrawerUrlParams.LayoutSettings,
    sidebarTabSearchParamValue: undefined,
  },
  /*
    {
      icon: <QuestionCircle />,
      content: 'Content 5',
      roles: [UserRoles.Underwriter, UserRoles.LIOContractor, UserRoles.Producer, UserRoles.SystemAdmin],
      quoteDetailsPath: '/',
      name: DrawerList.Help,
      sidebarItemSearchParamValue: undefined,
      sidebarTabSearchParamValue: undefined,
    },
    */
];
